import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../services/apiService';
import './SetupAgentPage.css';
import './ViewAgentPage.css';
import './AttributeRegistryPage.css';
import { 
  getAvailableEnvironments, 
  getSelectedEnvironment, 
  saveSelectedEnvironment,
  getApiEndpoints,
  ENVIRONMENT_LABELS,
  getEnvironmentColor
} from '../utils/apiConfig';

const AVAILABLE_OPERATORS = [
  'EQUALS',
  'NOT_EQUALS',
  'IN',
  'NOT_IN',
  'EXISITS',
  'NOT_EXISTS'
];

const AttributeRegistryPage = () => {
  const [attributes, setAttributes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [attributeRegistryForm, setAttributeRegistryForm] = useState({
    instanceId: '',
    attributeId: '',
    attributeName: '',
    displayName: '',
    dataType: 'String',
    allowedOperators: {},
    allowedValues: [{ value: '', displayName: '' }],
    active: true
  });
  const [instances, setInstances] = useState([]);
  const [loadingInstances, setLoadingInstances] = useState(false);
  const [loadingAttributes, setLoadingAttributes] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');


  // Environment selection
  const [selectedEnvironment, setSelectedEnvironment] = useState(getSelectedEnvironment());
  const availableEnvironments = getAvailableEnvironments();

  // Get API endpoints based on selected environment
  const apiEndpoints = getApiEndpoints(selectedEnvironment);
  const INSTANCE_DROPDOWN_API = apiEndpoints.instanceDropdown;
  const ATTRIBUTES_VIEW_API = apiEndpoints.attributesView;
  const ATTRIBUTE_REGISTRY_API = apiEndpoints.attributeRegistry;

  // Handle environment change
  const handleEnvironmentChange = (e) => {
    const newEnvironment = e.target.value;
    setSelectedEnvironment(newEnvironment);
    saveSelectedEnvironment(newEnvironment);
    setResult(null);
    setError(null);
  };

  const handleAttributeRegistryFormChange = (e) => {
    const { name, value, checked, type } = e.target;

    setAttributeRegistryForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (name === 'instanceId') {
      setError(null);
      setResult(null);
      if (value) {
        fetchAttributeList(value);
      } else {
        setAttributes([]);
      }
    }
  };

  const handleAllowedValueChange = (index, field, value) => {
    setAttributeRegistryForm(prev => ({
      ...prev,
      allowedValues: prev.allowedValues.map((allowedValue, allowedValueIndex) => (
        allowedValueIndex === index ? { ...allowedValue, [field]: value } : allowedValue
      ))
    }));
  };

  const handleAllowedOperatorsChange = (e) => {
    const selectedOperators = Array.from(e.target.selectedOptions, (option) => option.value);
    const allowedOperators = selectedOperators.reduce((operators, operator) => ({
      ...operators,
      [operator]: true
    }), {});

    setAttributeRegistryForm(prev => ({ ...prev, allowedOperators }));
  };

  const addAllowedValue = () => {
    setAttributeRegistryForm(prev => ({
      ...prev,
      allowedValues: [...prev.allowedValues, { value: '', displayName: '' }]
    }));
  };

  const removeAllowedValue = (index) => {
    setAttributeRegistryForm(prev => ({
      ...prev,
      allowedValues: prev.allowedValues.filter((_, allowedValueIndex) => allowedValueIndex !== index)
    }));
  };

  const loadAttribute = (attribute) => {
    setAttributeRegistryForm((prev) => ({
      instanceId: prev.instanceId,
      attributeId: attribute.attributeId || '',
      attributeName: attribute.attributeName || '',
      displayName: attribute.displayName || '',
      dataType: attribute.dataType || 'String',
      allowedOperators: { ...(attribute.allowedOperators || {}) },
      allowedValues: (attribute.allowedValues || []).map((allowedValue) => ({ ...allowedValue })),
      active: attribute.active !== false
    }));
    setError(null);
    setResult({ message: `Loaded attribute '${attribute.attributeId}'.` });
  };

   // Fetch agents for a specific instance
  const fetchAttributeList = async (instanceId) => {
    setLoadingAttributes(true);
    
    setAttributes([]);
    
    try {
      // Get user role from localStorage
      const userString = localStorage.getItem('user');
      const user = userString ? JSON.parse(userString) : null;
      const userRole = user?.role || '';

      const response = await apiService.post(ATTRIBUTES_VIEW_API, {
        instance_id: instanceId,
        user_role: userRole
      });
      
      if (response.data.status === 'ok') {
        setAttributes(response.data.result?.attributes || []);
        setPage(1);
      } else {
        console.error('Failed to load attributes:', response.data.message);
        setError('Failed to load attributes');
      }
    } catch (err) {
      console.error('Error fetching attributes:', err);
      setError('Error fetching attributes: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoadingAttributes(false);
    }
  };

 const handleAttributeSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const userString = localStorage.getItem('user');
      const user = userString ? JSON.parse(userString) : null;
      const response = await apiService.post(ATTRIBUTE_REGISTRY_API, {
        ...attributeRegistryForm,
        user_role: user?.role || ''
      });

      if (response.data.status !== 'ok') {
        throw new Error(response.data.message || 'Failed to create attribute');
      }

      await fetchAttributeList(attributeRegistryForm.instanceId);
      setAttributeRegistryForm(prev => ({
        ...prev,
        attributeId: '',
        attributeName: '',
        displayName: '',
        dataType: 'String',
        allowedOperators: {},
        allowedValues: [{ value: '', displayName: '' }],
        active: true
      }));
      setResult({ status: 'ok', message: 'Application attribute created successfully.' });
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to create attribute');
    } finally {
      setLoading(false);
    }
  };


  const getSortValue = (attribute, key) => {
    if (key === 'allowedValues') {
      return (attribute.allowedValues || [])
        .map((allowedValue) => `${allowedValue.value} ${allowedValue.displayName}`)
        .join(' ')
        .toLowerCase();
    }
    if (key === 'allowedOperators') {
      return Object.keys(attribute.allowedOperators || {}).join(' ').toLowerCase();
    }
    return String(attribute[key] ?? '').toLowerCase();
  };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(1);
  };

  const sortIndicator = (key) => (sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');

  const filteredAttributes = React.useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return attributes;
    return attributes.filter((attribute) => {
      return [
        attribute.attributeId,
        attribute.attributeName,
        attribute.displayName,
        attribute.dataType,
        ...Object.keys(attribute.allowedOperators || {}),
        attribute.active ? 'active' : 'inactive',
        ...(attribute.allowedValues || []).flatMap((allowedValue) => [allowedValue.value, allowedValue.displayName])
      ].some((value) => String(value || '').toLowerCase().includes(term));
    });
  }, [attributes, searchTerm]);

  const sortedAttributes = React.useMemo(() => {
    if (!sortKey) return filteredAttributes;
    const copy = [...filteredAttributes];
    copy.sort((a, b) => {
      const va = getSortValue(a, sortKey);
      const vb = getSortValue(b, sortKey);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return copy;
  }, [filteredAttributes, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sortedAttributes.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pagedAttributes = sortedAttributes.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setPage(1);
  };  

  // Fetch Connect instances on component mount and when environment changes
  useEffect(() => {
    const fetchInstances = async () => {
      setLoadingInstances(true);
      try {
        const response = await apiService.get(INSTANCE_DROPDOWN_API);
        if (response.data.status === 'ok') {
          setInstances(response.data.instances || []);
        } else {
          console.error('Failed to load instances:', response.data.message);
        }
      } catch (err) {
        console.error('Error fetching instances:', err);
      } finally {
        setLoadingInstances(false);
      }
    };
    fetchInstances();
  }, [INSTANCE_DROPDOWN_API, selectedEnvironment]);

  return (
      <div className="setup-page">
       <div className="page-header">
        <div>
            <h1>View Attributes</h1>
              <p className="page-subtitle">Manage Connect Contact and Application attributes</p>
        </div>
        <Link to="/routing-admin" className="btn btn-secondary">
          ← Back to Routing Administration
        </Link>
       </div>

      {/* Environment Selector */}
      <div className="environment-selector" style={{
        backgroundColor: '#f9f9f9',
        border: '2px solid #e0e0e0',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <label htmlFor="environment" style={{ 
          fontWeight: 'bold', 
          fontSize: '16px',
          minWidth: '120px'
        }}>
          Environment:
        </label>
        <select
          id="environment"
          value={selectedEnvironment}
          onChange={handleEnvironmentChange}
          style={{
            padding: '10px 16px',
            fontSize: '15px',
            borderRadius: '6px',
            border: '2px solid #ccc',
            backgroundColor: 'white',
            cursor: 'pointer',
            fontWeight: '500',
            minWidth: '200px'
          }}
        >
          {availableEnvironments.map(env => (
            <option key={env} value={env}>
              {ENVIRONMENT_LABELS[env]} ({env})
            </option>
          ))}
        </select>
        <span style={{
          padding: '6px 12px',
          borderRadius: '4px',
          fontSize: '13px',
          fontWeight: 'bold',
          color: 'white',
          backgroundColor: getEnvironmentColor(selectedEnvironment)
        }}>
          {selectedEnvironment}
        </span>
      </div>

      <div className="form-container">
            <form onSubmit={handleAttributeSubmit} className="setup-form">
                <div className="form-section">
                <h3>New Application Attribute</h3>
                    <div className="form-group">
                        <label htmlFor="instanceId">Instance *</label>
                        <select
                        id="instanceId"
                        name="instanceId"
                        value={attributeRegistryForm.instanceId}
                        onChange={handleAttributeRegistryFormChange}
                        required
                        disabled={loadingInstances}
                        >
                        <option value="">
                            {loadingInstances ? 'Loading instances...' : 'Select an instance...'}
                        </option>
                        {instances.map((instance) => (
                            <option key={instance.id} value={instance.id}>
                            {instance.name} ({instance.status})
                            </option>
                        ))}
                        </select>
                        <small>Select an Amazon Connect instance</small>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="attributeId">Attribute ID *</label>
                        <input
                          id="attributeId"
                          name="attributeId"
                          type="text"
                          value={attributeRegistryForm.attributeId}
                          onChange={handleAttributeRegistryFormChange}
                          required
                          maxLength={256}
                          placeholder="EMPLOYEE_TYPE"
                        />
                        <small>Stable identifier used by routing criteria</small>
                      </div>
                      <div className="form-group">
                        <label htmlFor="attributeName">Attribute Name *</label>
                        <input
                          id="attributeName"
                          name="attributeName"
                          type="text"
                          value={attributeRegistryForm.attributeName}
                          onChange={handleAttributeRegistryFormChange}
                          required
                          maxLength={256}
                          placeholder="employeeType"
                        />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="displayName">Display Name *</label>
                        <input
                          id="displayName"
                          name="displayName"
                          type="text"
                          value={attributeRegistryForm.displayName}
                          onChange={handleAttributeRegistryFormChange}
                          required
                          maxLength={256}
                          placeholder="Employee Type"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="dataType">Data Type *</label>
                        <select
                          id="dataType"
                          name="dataType"
                          value={attributeRegistryForm.dataType}
                          onChange={handleAttributeRegistryFormChange}
                          required
                        >
                          <option value="String">String</option>
                          <option value="Number">Number</option>
                          <option value="Boolean">Boolean</option>
                        </select>
                      </div>
                    </div>
                    <div className="form-group">
                      <label htmlFor="allowedOperators">Allowed Operators *</label>
                      <select
                        id="allowedOperators"
                        name="allowedOperators"
                        multiple
                        value={Object.keys(attributeRegistryForm.allowedOperators)}
                        onChange={handleAllowedOperatorsChange}
                        required
                      >
                        {AVAILABLE_OPERATORS.map((operator) => (
                          <option key={operator} value={operator}>{operator}</option>
                        ))}
                      </select>
                      <small>Select one or more operators supported by this attribute</small>
                    </div>
                    <fieldset className="allowed-values-fieldset">
                      <legend>Allowed Values *</legend>
                      {attributeRegistryForm.allowedValues.map((allowedValue, index) => (
                        <div className="allowed-value-row" key={index}>
                          <div className="form-group">
                            <label htmlFor={`allowedValue-${index}`}>Value</label>
                            <input
                              id={`allowedValue-${index}`}
                              type="text"
                              value={allowedValue.value}
                              onChange={(e) => handleAllowedValueChange(index, 'value', e.target.value)}
                              required
                              maxLength={256}
                              placeholder="INTERNAL"
                            />
                          </div>
                          <div className="form-group">
                            <label htmlFor={`allowedDisplayName-${index}`}>Display Name</label>
                            <input
                              id={`allowedDisplayName-${index}`}
                              type="text"
                              value={allowedValue.displayName}
                              onChange={(e) => handleAllowedValueChange(index, 'displayName', e.target.value)}
                              required
                              maxLength={256}
                              placeholder="Internal"
                            />
                          </div>
                          <button
                            type="button"
                            className="btn btn-secondary allowed-value-remove"
                            onClick={() => removeAllowedValue(index)}
                            disabled={attributeRegistryForm.allowedValues.length === 1}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                      <button type="button" className="btn btn-secondary" onClick={addAllowedValue}>
                        Add Allowed Value
                      </button>
                    </fieldset>
                    <div className="form-group attribute-active-control">
                      <label htmlFor="active">
                        <input
                          id="active"
                          name="active"
                          type="checkbox"
                          checked={attributeRegistryForm.active}
                          onChange={handleAttributeRegistryFormChange}
                        />
                        Active
                      </label>
                    </div>
                </div>

                <div className="form-actions">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading || loadingAttributes}
                  >
                    {loading ? 'Creating...' : 'Create Application Attribute'}
                  </button>
              </div>
            </form>
        </div>                

        {(loading || loadingAttributes) && (
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Processing your request...</p>
          </div>
        )}

        {attributes.length > 0 && (
          <div className="table-format-list">
            <div className="table-format-list-header">
              <h3>Attributes ({sortedAttributes.length}{sortedAttributes.length !== attributes.length ? ` of ${attributes.length}` : ''})</h3>
              <div className="table-format-list-controls">
                <input
                  type="text"
                  className="table-format-search-input"
                  placeholder="Search attributes..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                />
                <div className="page-size-selector">
                  <label htmlFor="page_size">Rows per page:</label>
                  <select id="page_size" value={pageSize} onChange={handlePageSizeChange}>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>
            </div>
            <table>
              <thead>
                <tr>
                  <th className="sortable" onClick={() => handleSort('attributeId')}>ID{sortIndicator('attributeId')}</th>
                  <th className="sortable" onClick={() => handleSort('attributeName')}>Name{sortIndicator('attributeName')}</th>
                  <th className="sortable" onClick={() => handleSort('displayName')}>Display Name{sortIndicator('displayName')}</th>
                  <th className="sortable" onClick={() => handleSort('dataType')}>Data Type{sortIndicator('dataType')}</th>
                  <th className="sortable" onClick={() => handleSort('allowedOperators')}>Allowed Operators{sortIndicator('allowedOperators')}</th>
                  <th className="sortable" onClick={() => handleSort('allowedValues')}>Allowed Values{sortIndicator('allowedValues')}</th>
                  <th className="sortable" onClick={() => handleSort('active')}>Status{sortIndicator('active')}</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedAttributes.map((attribute) => (
                  <tr key={attribute.attributeId}>
                    <td>{attribute.attributeId || '-'}</td>
                    <td>{attribute.attributeName || '-'}</td>
                    <td>{attribute.displayName || '-'}</td>
                    <td>{attribute.dataType || '-'}</td>
                    <td>{Object.keys(attribute.allowedOperators || {}).join(', ') || '-'}</td>
                    <td>{attribute.allowedValues?.map((allowedValue) => `${allowedValue.displayName} (${allowedValue.value})`).join(', ') || '-'}</td>
                    <td>{attribute.active ? 'Active' : 'Inactive'}</td>
                    <td>
                      <button type="button" className="btn btn-secondary" onClick={() => loadAttribute(attribute)}>
                        Load
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="pagination-controls">
              <button type="button" className="btn btn-secondary" onClick={() => setPage(1)} disabled={currentPage === 1}>« First</button>
              <button type="button" className="btn btn-secondary" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>‹ Prev</button>
              <span className="pagination-status">Page {currentPage} of {totalPages}</span>
              <button type="button" className="btn btn-secondary" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Next ›</button>
              <button type="button" className="btn btn-secondary" onClick={() => setPage(totalPages)} disabled={currentPage === totalPages}>Last »</button>
            </div>
          </div>
        )}

        {!loading && attributes.length === 0 && (
          <div className="attribute-list">
            <h3>Attributes ({attributes.length})</h3>
            <p>No attributes found.</p>
          </div>
        )}

      {error && (
        <div className="alert alert-error">
          <h4>Error</h4>
          <p>{error}</p>
        </div>
      )}

      {result && (
        <div className="alert alert-success">
          <h4>Success</h4>
          <p>{result.message}</p>
        </div>
      )}

    </div>
    );
};

export default AttributeRegistryPage;